import { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Raven from 'raven-js';
import useVariants from 'personalization-lib/hooks/variants/useVariants';
import useContentCrmObject from 'ContentEditorUI/components/publishing/hooks/useContentCrmObject';
import { getIsPublished } from 'ContentEditorUI/redux/selectors/publishSelectors';
// @ts-ignore not yet migrated
import { getLastSaveDate } from 'ContentEditorUI/redux/selectors/editingInfoSelectors';
import { getContentName } from 'content-management-data-lib';
import { getContentModelPersonalizationStrategyId, getIsUsingPersonalization } from 'page-editor-ui/redux/selectors/pageContentModelSelectors';
export const useUpdateVariantSelectorOnSave = () => {
  var _contentCrmObject$pro;
  const personalizationStrategyId = useSelector(getContentModelPersonalizationStrategyId);
  const isUsingPersonalization = useSelector(getIsUsingPersonalization);

  // Skip if no personalization strategy id is set
  const {
    refetch: refetchPersonalizationVariants
  } = useVariants(personalizationStrategyId ? Number(personalizationStrategyId) : undefined, error => {
    Raven.captureMessage('[P13n] - Failed to load variants', {
      extra: {
        error
      }
    });
  });
  const {
    contentCrmObject
  } = useContentCrmObject();
  const publishedAt = contentCrmObject === null || contentCrmObject === void 0 || (_contentCrmObject$pro = contentCrmObject.properties['hs_published_at']) === null || _contentCrmObject$pro === void 0 ? void 0 : _contentCrmObject$pro.value;
  const previousPublishedAt = useRef(publishedAt);
  const internalPageName = useSelector(getContentName);
  const previousInternalPageName = useRef(internalPageName);
  const isPublished = useSelector(getIsPublished);
  const previousIsPublished = useRef(isPublished);
  const lastSavedAt = useSelector(getLastSaveDate);
  const previousLastSavedAt = useRef(lastSavedAt);
  const hasBeenSavedSinceLastCheck = lastSavedAt !== previousLastSavedAt.current;
  const hasPublishStatusChanged = previousIsPublished.current !== isPublished;
  const hasPageNameChanged = internalPageName !== previousInternalPageName.current;
  const hasPublishedAtChanged = publishedAt !== previousPublishedAt.current;
  useEffect(() => {
    // Skip if not using personalization
    if (!isUsingPersonalization) {
      return;
    }

    // If the publish status has changed, refetch the variants
    if (hasPublishStatusChanged) {
      refetchPersonalizationVariants();
      previousIsPublished.current = isPublished;
      previousLastSavedAt.current = lastSavedAt;
      previousPublishedAt.current = publishedAt;
      previousInternalPageName.current = internalPageName;
      return;
    }

    // If the page is not published and the page name has changed, refetch the variants
    if (!isPublished && hasPageNameChanged && hasBeenSavedSinceLastCheck) {
      refetchPersonalizationVariants();
      previousInternalPageName.current = internalPageName;
      previousLastSavedAt.current = lastSavedAt;
      return;
    }

    // If the page is published and the published at has changed , refetch the variants
    // This case is the most interesting because a name change by itself doesn't trigger a refetch
    // That's because we save to a buffered state when published, and the personalization lib can't ready from that data
    // We have to wait until the next "hard save", which happens on publish
    // Consequentially, we may have an extra refetch here if the name did NOT change, but this is acceptable and rare
    if (isPublished && hasPublishedAtChanged) {
      refetchPersonalizationVariants();
      previousPublishedAt.current = publishedAt;
      previousIsPublished.current = isPublished;
      previousLastSavedAt.current = lastSavedAt;
      previousInternalPageName.current = internalPageName;
      return;
    }
  }, [lastSavedAt, isPublished, refetchPersonalizationVariants, internalPageName, previousIsPublished, hasBeenSavedSinceLastCheck, hasPublishStatusChanged, hasPageNameChanged, hasPublishedAtChanged, publishedAt, isUsingPersonalization]);
  return null;
};
const VariantSelectUpdateWrapper = () => {
  useUpdateVariantSelectorOnSave();
  return null;
};
export default VariantSelectUpdateWrapper;